import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search, GitBranch, X } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Combobox, Field, IconButton, Input, Tabs, Textarea, Toggle,
} from '@/components/ui';

import { CostCenterTreePanel } from '@/components/erp/CostCenterTreePanel';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { costCenterApi }       from '@/api/costCenter.api';
import { locationApi }         from '@/api/location.api';
import type {
  CostCenterDto, CostCenterListDto, CostCenterTreeNodeDto,
} from '@/types/costCenter.types';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  code:         z.string().min(1, 'Code is required').max(20),
  name:         z.string().min(1, 'Name is required').max(200),
  shortName:    z.string().max(50).nullable().optional(),
  parentId:     z.string().uuid().nullable().optional(),
  // Required, not optional. A cost centre with no location is hidden from every voucher picker by
  // design, so saving one without a location produces a record that can never be used (QC Part III
  // item 1). The field simply did not exist before, which is why untagged ones exist at all.
  locationId:   z.string().uuid({ message: 'Choose the location this cost centre belongs to' }),
  level:        z.coerce.number().int().min(0),
  isActive:     z.boolean(),
  allowPosting: z.boolean(),
  budgetControl: z.boolean(),
  description:  z.string().max(500).nullable().optional(),
  remarks:      z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

function makeDefaults(code = ''): FormValues {
  return {
    code, name: '', shortName: null, parentId: null, locationId: '', level: 1,
    isActive: true, allowPosting: true, budgetControl: false,
    description: null, remarks: null,
  };
}

const TABS = ['General', 'Settings', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<CostCenterListDto>[] = [
  { key: 'code',     label: 'Code',  className: 'w-24 font-mono' },
  { key: 'name',     label: 'Name' },
  { key: 'level',    label: 'Lvl',   className: 'w-12 text-center' },
  { key: 'allowPosting', label: 'Mode', className: 'w-20',
    render: r => <Badge variant={r.allowPosting ? 'default' : 'success'}>{r.allowPosting ? 'Posting' : 'Header'}</Badge> },
  { key: 'isActive', label: 'Status', className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

const PARENT_COLUMNS: SearchColumn<CostCenterListDto>[] = [
  { key: 'code',  label: 'Code', className: 'w-24 font-mono' },
  { key: 'name',  label: 'Name' },
  { key: 'level', label: 'Lvl',  className: 'w-12 text-center' },
];

function dtoToForm(dto: CostCenterDto): FormValues {
  return {
    code: dto.code, name: dto.name, shortName: dto.shortName ?? null,
    parentId: dto.parentId ?? null, locationId: dto.locationId ?? '', level: dto.level,
    isActive: dto.isActive, allowPosting: dto.allowPosting, budgetControl: dto.budgetControl,
    description: dto.description ?? null, remarks: dto.remarks ?? null,
  };
}

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-gray-100 dark:border-gray-700">
    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{children}</h3>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const CostCenterPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_COST_CENTER_VIEW);
  const canCreate = hasPermission(Permissions.MST_COST_CENTER_CREATE);
  const canEdit   = hasPermission(Permissions.MST_COST_CENTER_EDIT);
  const canDelete = hasPermission(Permissions.MST_COST_CENTER_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [codeInput,      setCodeInput]    = useState('');

  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  // Parent lookup state
  const [parentDisplay, setParentDisplay] = useState<{ id: string | null; code: string | null; name: string | null }>({
    id: null, code: null, name: null,
  });
  const [parentSearchOpen, setParentSearchOpen] = useState(false);

  // Tree state
  const [treeNodes,      setTreeNodes]      = useState<CostCenterTreeNodeDto[]>([]);

  // The locations this cost centre can belong to. Company-scoped by the server.
  const [locations, setLocations] = useState<{ id: string; code: string; name: string }[]>([]);

  useEffect(() => {
    locationApi.getList({ pageSize: 200, isActive: true })
      .then(r => setLocations(r.data.data?.items ?? []))
      .catch(() => {});
  }, []);
  const [treeLoading,    setTreeLoading]    = useState(false);
  const [treeSelectedId, setTreeSelectedId] = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: makeDefaults(),
  });

  const isActive     = watch('isActive');
  const allowPosting = watch('allowPosting');
  const budgetControl = watch('budgetControl');
  const fieldsDisabled = screen.isInitial || screen.isView;

  // Tree loading
  const loadTree = useCallback(async () => {
    if (!canView) return;
    setTreeLoading(true);
    try {
      const res = await costCenterApi.getTree();
      setTreeNodes(res.data.data ?? []);
    } catch { /* tree is optional */ }
    finally { setTreeLoading(false); }
  }, [canView]);

  useEffect(() => { loadTree(); }, [loadTree]);

  const master = useMasterRecord<CostCenterDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(makeDefaults()),
    clearScreen: () => { setCodeInput(''); setParentDisplay({ id: null, code: null, name: null }); setTreeSelectedId(null); },
    onDelete: {
      remove:   id => costCenterApi.remove(id),
      describe: r => `Cost centre '${r.code}'`,
      after:    () => loadTree(),
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: CostCenterDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setCodeInput(dto.code);
    screen.setMode('view'); setActiveTab('General'); setTreeSelectedId(dto.id);
    // parent display — parentId set in form but we don't have parentCode/name in the DTO directly
    // We'll just show ID for now; real apps would include parentCode in DTO
    setParentDisplay({ id: dto.parentId ?? null, code: null, name: null });
  }, [reset, screen]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view cost centres.'); return; }
    setLookupBusy(true);
    try {
      const res = await costCenterApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Cost centre '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset(makeDefaults(code)); setCodeInput(code);
    setParentDisplay({ id: null, code: null, name: null });
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleTreeSelect = (node: CostCenterTreeNodeDto) => {
    if (screen.isNew || screen.isEdit) return;
    setTreeSelectedId(node.id);
    setLookupBusy(true);
    costCenterApi.getById(node.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load cost centre.'))
      .finally(() => setLookupBusy(false));
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await costCenterApi.create({
          code: d.code.trim().toUpperCase(), name: d.name.trim(),
          shortName: n(d.shortName), parentId: parentDisplay.id,
          locationId: d.locationId,
          level: d.level, allowPosting: d.allowPosting,
          budgetControl: d.budgetControl, description: n(d.description),
        });
        flash('success', 'Cost centre created successfully.');
      } else if (screen.isEdit && record) {
        await costCenterApi.update(record.id, {
          name: d.name.trim(), shortName: n(d.shortName),
          parentId: parentDisplay.id, locationId: d.locationId, level: d.level,
          isActive: d.isActive, allowPosting: d.allowPosting,
          budgetControl: d.budgetControl, description: n(d.description),
          remarks: n(d.remarks),
        });
        flash('success', 'Cost centre updated successfully.');
      }
      await loadTree();
      resetToInitial();
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };


  const fetchCostCenters = useMemo(() => masterListFetcher(costCenterApi.getList), []);

  const fetchParentCostCenters = useCallback(async (search: string, page: number, pageSize: number) => {
    // Only header cost centres can be parents. Filtered on the server: filtering one page in the
    // browser left the picker empty whenever that page held only posting cost centres (QC P9-100).
    const res = await costCenterApi.getList({ search, pageNumber: page, pageSize, allowPosting: false, isActive: true });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const handleSearchSelect = (item: CostCenterListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    costCenterApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load cost centre.'))
      .finally(() => setLookupBusy(false));
  };

  const handleParentSelect = (item: CostCenterListDto) => {
    setParentSearchOpen(false);
    setParentDisplay({ id: item.id, code: item.code, name: item.name });
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">

      <MasterPageHeader icon={GitBranch} title="Cost Centre Maintenance"
        subtitle="Master File · Tree View" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Code"
        summary={record && (<>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate max-w-xs">{record.name}</span>
            <Badge variant={record.allowPosting ? 'default' : 'success'}>{record.allowPosting ? 'Posting' : 'Header'}</Badge>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">INACTIVE</span>
            )}
        </>)}
        hint={screen.isInitial ? "Enter a code, click the tree, or use the search icon." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search cost centres"
          placeholder="Enter code + ↵"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      {/* TREE_FORM layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* LEFT: Tree */}
        <div className="w-72 flex-shrink-0 overflow-hidden">
          <CostCenterTreePanel
            nodes={treeNodes}
            loading={treeLoading}
            selectedId={treeSelectedId}
            onSelect={handleTreeSelect}
            onRefresh={loadTree}
          />
        </div>

        {/* RIGHT: Form */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Tabs */}
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab}
            className="flex-shrink-0 px-0 static" />

          <div className="flex-1 overflow-y-auto">
            <form onSubmit={e => e.preventDefault()} noValidate>
              <div className="p-4 space-y-4 max-w-2xl">

                {/* General tab */}
                {activeTab === 'General' && (
                  <>
                    <SectionHeading>Identification</SectionHeading>
                    <div className="grid grid-cols-2 gap-3">
                      <Input label="Code *" {...register('code')} disabled
                        placeholder="— enter via code field above —"
                        className="font-mono uppercase text-xs" />
                      <Input label="Short Name" size="sm" {...register('shortName')}
                        disabled={fieldsDisabled} maxLength={50} placeholder="Abbreviated name"
                        className="px-2.5 text-xs" />
                      <Input label="Name *" {...register('name')} error={errors.name?.message}
                        disabled={fieldsDisabled} placeholder="Cost centre name" className="text-xs col-span-2" />
                    </div>

                    <SectionHeading>Hierarchy</SectionHeading>

                    {/* Location (QC Part III item 1). A cost centre belongs to exactly one
                        location, and one that belongs to none is offered on no voucher — so this
                        is required rather than optional. */}
                    <Controller name="locationId" control={control} render={({ field }) => (
                      <Combobox
                        label="Location *"
                        hint="A location has many cost centres; each belongs to one."
                        options={locations.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        disabled={fieldsDisabled}
                        error={errors.locationId?.message}
                      />
                    )} />

                    {/* Parent Cost Center lookup */}
                    <Field label="Parent Cost Centre" hint="Header cost centres only.">
                      <div className={cn(
                        'flex items-center h-9 rounded-md border text-sm',
                        'bg-white dark:bg-gray-800',
                        fieldsDisabled
                          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                          : 'border-gray-300 dark:border-gray-600',
                      )}>
                        <div className="flex-1 flex items-center gap-2 px-3 min-w-0">
                          {parentDisplay.id ? (
                            <>
                              <span className="font-mono text-xs text-indigo-600 dark:text-indigo-400 flex-shrink-0">{parentDisplay.code}</span>
                              {parentDisplay.name && <><span className="text-gray-500 dark:text-gray-400 flex-shrink-0">—</span>
                              <span className="text-gray-700 dark:text-gray-300 truncate">{parentDisplay.name}</span></>}
                            </>
                          ) : (
                            <span className="text-gray-400 italic text-xs">None — top-level cost centre</span>
                          )}
                        </div>
                        {parentDisplay.id && !fieldsDisabled && (
                          <IconButton
                            label="Clear parent cost centre"
                            variant="bare"
                            size="xs"
                            icon={<X size={13} />}
                            onClick={() => setParentDisplay({ id: null, code: null, name: null })}
                            className="flex-shrink-0 hover:text-rose-500"
                          />
                        )}
                        {!fieldsDisabled && (
                          <IconButton
                            label="Search parent cost centres"
                            variant="bare"
                            icon={<Search size={13} />}
                            onClick={() => setParentSearchOpen(true)}
                            className="flex-shrink-0 w-8 h-full rounded-none rounded-r-md border-l border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                          />
                        )}
                      </div>
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                      <Input label="Level" size="sm" type="number" {...register('level')}
                        disabled={fieldsDisabled} min={0} className="px-2.5 text-xs" />
                    </div>

                    <Textarea label="Description" {...register('description')} disabled={fieldsDisabled}
                      rows={2} maxLength={500} placeholder="Optional description…"
                      className="px-2.5 text-xs resize-none" fieldClassName="mt-2" />

                    {(screen.isEdit || screen.isView) && (
                      <>
                        <SectionHeading>Status</SectionHeading>
                        <Controller name="isActive" control={control} render={({ field }) => (
                          <Toggle label={isActive ? 'Active' : 'Inactive'}
                            description="Inactive cost centres are hidden from transaction entry."
                            checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                        )} />
                      </>
                    )}
                  </>
                )}

                {/* Settings tab */}
                {activeTab === 'Settings' && (
                  <>
                    <SectionHeading>Posting Rules</SectionHeading>
                    <Controller name="allowPosting" control={control} render={({ field }) => (
                      <Toggle
                        label={allowPosting ? 'Posting Cost Centre' : 'Header / Group Cost Centre'}
                        description={allowPosting
                          ? 'Transactions can be posted directly to this cost centre.'
                          : 'For grouping only — transactions cannot be posted here.'}
                        checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />

                    <SectionHeading>Budget Control</SectionHeading>
                    <Controller name="budgetControl" control={control} render={({ field }) => (
                      <Toggle
                        label="Budget Control"
                        description="Enforce budget limits when posting to this cost centre."
                        checked={field.value} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />

                    {budgetControl && (
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-[11px] text-blue-700 dark:text-blue-400">
                        Budget enforcement is active. Transactions exceeding the budget allocation will require override authorisation.
                      </div>
                    )}

                    <SectionHeading>Remarks</SectionHeading>
                    <Textarea {...register('remarks')} disabled={fieldsDisabled} rows={3}
                      maxLength={500} placeholder="Optional internal notes…"
                      className="px-2.5 text-xs resize-none" />
                  </>
                )}

                {/* Audit tab */}
                {activeTab === 'Audit' && <MasterAuditTab record={record} />}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modals */}
      <MasterSearchModal<CostCenterListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Cost Centres" columns={SEARCH_COLUMNS}
        fetchData={fetchCostCenters} onSelect={handleSearchSelect}
      />

      <MasterSearchModal<CostCenterListDto>
        isOpen={parentSearchOpen} onClose={() => setParentSearchOpen(false)}
        title="Select Parent Cost Centre" columns={PARENT_COLUMNS}
        fetchData={fetchParentCostCenters} onSelect={handleParentSelect}
      />

      <MasterConfirmDialogs
        entity="Cost centre"
        screen={screen}
        record={record}
        describe={r => `${r.code} — ${r.name}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Cost Centre Report">
        <ReportHeader reportTitle="Cost Centre Master Report"
          printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
          meta={record ? [
            { label: 'Code',   value: record.code },
            { label: 'Status', value: record.isActive ? 'Active' : 'Inactive' },
          ] : []}
        />
        {record && (
          <>
            <ReportSection title="Cost Centre Details" fields={[
              { label: 'Code',           value: record.code },
              { label: 'Name',           value: record.name, wide: true },
              { label: 'Short Name',     value: record.shortName },
              { label: 'Level',          value: record.level },
              { label: 'Allow Posting',  value: record.allowPosting, display: record.allowPosting ? 'Yes' : 'No' },
              { label: 'Budget Control', value: record.budgetControl, display: record.budgetControl ? 'Yes' : 'No' },
              { label: 'Status',         value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
              { label: 'Description',    value: record.description, wide: true },
            ]} />
            <ReportSection title="Audit Information" fields={[
              { label: 'Created By',    value: record.createdByName ?? record.createdBy },
              { label: 'Created Date',  value: formatDateTime(record.createdDate) },
              { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
              { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
            ]} />
          </>
        )}
        <ReportFooter />
      </MasterReportPreview>
    </div>
  );
};
